import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Gift, Search, Loader2, X, Check, AlertCircle, QrCode } from 'lucide-react';
import { api, compact } from '../lib/api';
import { Avatar, Sheet } from './ui';
import { rarityOf } from '../lib/classTheme';

export default function GiftModal({ open, onClose, item, onSuccess }) {
  const [username, setUsername] = useState('');
  const [message, setMessage] = useState('');
  const [found, setFound] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const r = item ? rarityOf(item.rarity) : null;
  const fee = item ? Math.max(10, Math.floor((item.sellPrice / 0.4) * 0.05)) : 0;

  useEffect(() => {
    if (!open) { setUsername(''); setMessage(''); setFound(null); setError(null); }
  }, [open]);

  /* live user search */
  useEffect(() => {
    const u = username.replace(/^@/, '').trim();
    if (u.length < 3) { setFound(null); setError(null); return; }

    const t = setTimeout(async () => {
      setSearching(true); setError(null);
      try {
        const { data } = await api.get('/users/search', { params: { q: u } });
        const exact = data.find(x => x.username.toLowerCase() === u.toLowerCase());
        if (exact) {
          const gear = await api.get(`/inventory/user/${exact.username}`).then(x => x.data).catch(() => null);
          setFound({ ...exact, gear });
          if (item && gear && exact.class_key && exact.class_key !== item.classKey) {
            setError(`@${exact.username} plays ${exact.class_key} — this is ${item.classKey} gear`);
          }
        } else {
          setFound(null);
          setError('User not found');
        }
      } catch { setFound(null); } finally { setSearching(false); }
    }, 500);
    return () => clearTimeout(t);
  }, [username, item]);

  const send = useMutation({
    mutationFn: () => api.post('/inventory/gift', {
      userItemId: item.id, to: found.username, message,
    }),
    onSuccess: ({ data }) => {
      toast.success(`🎁 Gift sent to @${data.to}!`, { duration: 5000 });
      onSuccess?.(data);
      onClose();
    },
    onError: e => { setError(e.message); toast.error(e.message); },
  });

  if (!item) return null;

  return (
    <Sheet open={open} onClose={onClose} title="Send as a gift">
      <div className="space-y-4">
        {/* item preview */}
        <div className="flex items-center gap-3 p-3 rounded-2xl border-2"
          style={{ borderColor: `${r.hex}55`, background: `${r.hex}0a` }}>
          <div className="w-16 h-16 rounded-xl overflow-hidden border-2 shrink-0"
            style={{ borderColor: r.hex }}>
            <img src={item.thumb || item.image} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="chip" style={{ background: `${r.hex}22`, color: r.hex }}>
              {r.label}
            </span>
            <p className="font-bold text-sm mt-1 truncate">{item.name}</p>
            <p className="text-[11px] text-ink-400 font-semibold">
              +{item.totalStats} stats · {item.slot}
            </p>
          </div>
        </div>

        {/* recipient */}
        <div>
          <label className="label">Send to</label>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input className="input pl-9" placeholder="username or referral code"
              value={username} autoFocus
              onChange={e => setUsername(e.target.value)} />
            {searching && (
              <Loader2 size={15} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-ink-400" />
            )}
          </div>
        </div>

        {/* found user */}
        {found && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className={`flex items-center gap-3 p-3 rounded-xl border-2
              ${error ? 'border-red-300 bg-red-50' : 'border-mint-400 bg-mint-400/10'}`}>
            <Avatar src={found.image_url || found.avatar_url} name={found.username} size={40} />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm truncate">{found.display_name || found.username}</p>
              <p className="text-[11px] text-ink-400 font-semibold">
                @{found.username}{found.level ? ` · Level ${found.level}` : ''}
                {found.gear?.gearScore ? ` · ${found.gear.gearScore} GS` : ''}
              </p>
            </div>
            {error
              ? <AlertCircle size={17} className="text-red-500 shrink-0" />
              : <Check size={17} className="text-mint-600 shrink-0" strokeWidth={3} />}
          </motion.div>
        )}

        {error && !found && (
          <p className="text-xs font-bold text-red-500 flex items-center gap-1.5">
            <AlertCircle size={13} /> {error}
          </p>
        )}

        {/* message */}
        <div>
          <label className="label">Message (optional)</label>
          <textarea className="input min-h-[70px]" maxLength={300}
            placeholder="Add a note…"
            value={message} onChange={e => setMessage(e.target.value)} />
        </div>

        {/* fee */}
        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
          <div className="flex items-center justify-between text-sm">
            <span className="font-bold text-amber-800">Transfer fee (5%)</span>
            <span className="font-black text-amber-700">💎 {compact(fee)}</span>
          </div>
          <p className="text-[11px] text-amber-700 font-semibold mt-1">
            The gift expires in 48h if not accepted. Max 3 gifts per day.
          </p>
        </div>

        <button onClick={() => send.mutate()}
          disabled={!found || !!error || send.isPending}
          className="btn-primary w-full py-3">
          {send.isPending
            ? <><Loader2 size={16} className="animate-spin" /> Sending…</>
            : <><Gift size={16} /> Send gift</>}
        </button>
      </div>
    </Sheet>
  );
}

/* ============================================================
   INCOMING GIFTS LIST
   ============================================================ */
export function GiftsInbox({ gifts, onAccept, onDecline, busy }) {
  if (!gifts?.incoming?.length && !gifts?.outgoing?.length) {
    return (
      <div className="card p-8 text-center">
        <Gift size={34} className="mx-auto text-ink-300 mb-2" />
        <p className="font-bold text-ink-600">No gifts yet</p>
        <p className="text-sm text-ink-400 mt-1">
          Send gear to friends from your inventory 🎁
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {gifts.incoming?.length > 0 && (
        <div>
          <p className="label">Incoming ({gifts.incoming.length})</p>
          <div className="space-y-2">
            {gifts.incoming.map(g => {
              const r = rarityOf(g.rarity);
              const hoursLeft = Math.max(0,
                Math.round((new Date(g.expires_at) - Date.now()) / 3600000));
              return (
                <motion.div key={g.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="card p-3 border-2" style={{ borderColor: `${r.hex}55` }}>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-xl overflow-hidden border-2 shrink-0"
                      style={{ borderColor: r.hex }}>
                      {g.thumb_url || g.image_url
                        ? <img src={g.thumb_url || g.image_url} alt="" className="w-full h-full object-cover" />
                        : <div className="w-full h-full grid place-items-center text-xl">{g.icon}</div>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="chip" style={{ background: `${r.hex}22`, color: r.hex }}>
                        {g.rarity_label}
                      </span>
                      <p className="font-bold text-sm mt-0.5 truncate">{g.name}</p>
                      <p className="text-[11px] text-ink-400 font-semibold">
                        from @{g.from_username} · {hoursLeft}h left
                      </p>
                    </div>
                  </div>

                  {g.message && (
                    <p className="text-xs text-ink-600 italic mt-2 px-2 py-1.5 rounded-lg bg-ink-50">
                      “{g.message}”
                    </p>
                  )}

                  <div className="grid grid-cols-2 gap-2 mt-2.5">
                    <button onClick={() => onDecline(g.id)} disabled={busy}
                      className="btn-ghost btn-sm">Decline</button>
                    <button onClick={() => onAccept(g.id)} disabled={busy}
                      className="btn-mint btn-sm font-black">
                      <Check size={13} /> Accept
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {gifts.outgoing?.length > 0 && (
        <div>
          <p className="label">Sent</p>
          <div className="card p-2 space-y-1">
            {gifts.outgoing.slice(0, 10).map(g => {
              const r = rarityOf(g.rarity);
              const st = {
                pending: { l: 'Pending', c: 'chip-ink' },
                accepted: { l: 'Accepted', c: 'chip-mint' },
                declined: { l: 'Declined', c: 'bg-red-100 text-red-600 chip' },
                expired: { l: 'Expired', c: 'chip-ink' },
                cancelled: { l: 'Cancelled', c: 'chip-ink' },
              }[g.status] || { l: g.status, c: 'chip-ink' };
              return (
                <div key={g.id} className="flex items-center gap-2.5 p-2 rounded-xl hover:bg-ink-50">
                  <span className="text-lg">{g.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-xs truncate">{g.name}</p>
                    <p className="text-[10px] text-ink-400 font-semibold">
                      to @{g.to_username}
                    </p>
                  </div>
                  <span className={st.c}>{st.l}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}